import React from 'react';
import { Card } from './ui/card';
import { MapPin, Gamepad2, Waves, Dumbbell, Tv, Users } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

const StadiumActivities = ({ data }) => {
  const { language } = useLanguage();
  
  // Get translated data
  const translatedData = getTranslation(language, 'activities');
  const title = translatedData?.title || 'Beyond Training';
  const subtitle = translatedData?.subtitle || 'Stadium visits, activities, and experiences that make your camp unforgettable';
  const stadiumVisits = translatedData?.stadiumVisits || data.stadiumVisits;
  const freetimeTitle = translatedData?.freetimeTitle || 'Freetime Activities';

  // Activity labels translations
  const activityLabels = {
    en: ['Swimming', 'Games Room', 'PlayStation', 'Gymnasium', 'Team Building', 'Presentations'],
    es: ['Natación', 'Sala de Juegos', 'PlayStation', 'Gimnasio', 'Trabajo en Equipo', 'Presentaciones'],
    fr: ['Natation', 'Salle de Jeux', 'PlayStation', 'Salle de Sport', 'Team Building', 'Présentations']
  };
  
  const labels = activityLabels[language] || activityLabels.en;

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black mb-4">
            {title}
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Stadium Visits */}
        <div className="mb-16">
          <h3 className="text-3xl font-black mb-8 text-center">
            <MapPin className="inline h-8 w-8 text-green-500 mr-3" />
            {language === 'es' ? 'Visitas a Estadios Icónicos' : language === 'fr' ? 'Visites de Stades Emblématiques' : 'Iconic Stadium Visits'}
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {stadiumVisits.map((stadium, index) => (
              <Card 
                key={index}
                className="overflow-hidden group hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative h-64">
                  <img 
                    src={data.stadiumVisits[index]?.image || ''} 
                    alt={stadium.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end">
                    <div className="p-6">
                      <h4 className="text-2xl font-black text-white mb-2">{stadium.name}</h4>
                      <p className="text-gray-200">{stadium.description}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Freetime Activities */}
        <div>
          <h3 className="text-3xl font-black mb-8 text-center">
            {freetimeTitle}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="p-4 bg-white/10 backdrop-blur-sm border-white/20 text-center hover:bg-white/20 transition-all">
              <Waves className="h-8 w-8 mx-auto mb-2 text-blue-400" />
              <p className="text-sm font-bold">{labels[0]}</p>
            </Card>
            <Card className="p-4 bg-white/10 backdrop-blur-sm border-white/20 text-center hover:bg-white/20 transition-all">
              <Gamepad2 className="h-8 w-8 mx-auto mb-2 text-green-400" />
              <p className="text-sm font-bold">{labels[1]}</p>
            </Card>
            <Card className="p-4 bg-white/10 backdrop-blur-sm border-white/20 text-center hover:bg-white/20 transition-all">
              <Tv className="h-8 w-8 mx-auto mb-2 text-purple-400" />
              <p className="text-sm font-bold">{labels[2]}</p>
            </Card>
            <Card className="p-4 bg-white/10 backdrop-blur-sm border-white/20 text-center hover:bg-white/20 transition-all">
              <Dumbbell className="h-8 w-8 mx-auto mb-2 text-orange-400" />
              <p className="text-sm font-bold">{labels[3]}</p>
            </Card>
            <Card className="p-4 bg-white/10 backdrop-blur-sm border-white/20 text-center hover:bg-white/20 transition-all">
              <Users className="h-8 w-8 mx-auto mb-2 text-pink-400" />
              <p className="text-sm font-bold">{labels[4]}</p>
            </Card>
            <Card className="p-4 bg-white/10 backdrop-blur-sm border-white/20 text-center hover:bg-white/20 transition-all">
              <MapPin className="h-8 w-8 mx-auto mb-2 text-yellow-400" />
              <p className="text-sm font-bold">{labels[5]}</p>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StadiumActivities;